import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'
import styled from 'styled-components'
import SectionContext from '../../../../../contexts/SectionContext'
import UserContext from '../../../../../contexts/UserContext'
import { Eye, Pencil, Trash2 } from 'lucide-react'
import { EquipmentOutletType } from '../../index'
import ViewEquipmentModal from '../ViewEquipmentModal'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../../components/ModalConfirmation'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../../components/StandardPage'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

const machinesScreenKey = 'equipment_machines'

const MAINTENANCE_DATE_FILTER_KEYS = ['lastMaintenance', 'nextMaintenance']

const machineMatchesMaintenanceDateFilter = (
  isoDate: string | undefined,
  filter: any,
): boolean => {
  if (!isoDate) return false
  const dateValue = isoDate.split('T')[0]
  if (filter.filterType === 'Intervalo') {
    const start = filter.value?.start
    const end = filter.value?.end
    if (!start || !end) return true
    return dateValue >= start && dateValue <= end
  }
  const filterDate = String(filter.value || '')
  if (!filterDate) return true
  if (filter.filterType === 'Antes de') return dateValue < filterDate
  if (filter.filterType === 'Depois de') return dateValue > filterDate
  return dateValue === filterDate
}

interface BackendMachineListItem {
  id: number
  name: string
  model: string
  costPerHour: number
  lastMaintenanceDate?: string
  nextMaintenanceDate?: string
}

interface MachineData {
  id: number
  machineName: string
  model: string
  costPerHour: string
  lastMaintenance: string
  nextMaintenance: string
}

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const InfoMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #8c98a4;
  font-size: 16px;
`

const buildMachinesColumns = (): StandardColumn<MachineData>[] => [
  { id: 'machineName', label: 'Máquina', accessor: (machine) => machine.machineName },
  { id: 'model', label: 'Modelo', accessor: (machine) => machine.model },
  { id: 'costPerHour', label: 'Custo por hora', accessor: (machine) => machine.costPerHour },
  { id: 'lastMaintenance', label: 'Última manutenção', accessor: (machine) => machine.lastMaintenance },
  { id: 'nextMaintenance', label: 'Próxima manutenção', accessor: (machine) => machine.nextMaintenance },
]

const EquipmentTable: React.FC = () => {
  const { refresh, search, appliedFilters } =
    useOutletContext<EquipmentOutletType>()
  const navigate = useNavigate()

  const [machines, setMachines] = useState<MachineData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showViewModal, setShowViewModal] = useState<boolean>(false)
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(
    null,
  )
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [machineToDelete, setMachineToDelete] = useState<number | null>(null)

  const { permissions } = useContext(SectionContext)
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => buildMachinesColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(machinesScreenKey, columns)

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return 'N/A'
    const [datePart] = dateString.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    if (year && month && day) {
      const parsedDate = new Date(year, month - 1, day)
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString('pt-BR')
      }
    }
    const fallbackDate = new Date(dateString)
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate.toLocaleDateString('pt-BR')
    }
    return dateString
  }, [])

  const formatCurrency = useCallback((value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue) || numValue === null || numValue === undefined) {
      return 'R$ N/A'
    }
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    })
  }, [])

  const canDeleteEquipment = useCallback((): boolean => {
    if (user?.role === 'ADMIN_GLOBAL' || user?.adminUser) return true

    const perms: string[] = Array.isArray(permissions) ? permissions : []
    const allowedKeys = ['equipment.delete', 'delete-equipment', 'delete_equipment', 'equipment']
    return allowedKeys.some((k) => perms.includes(k))
  }, [permissions, user])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const params: any = {}
      if (search && search.trim() !== '') {
        params.search = search
      }
      const maintenanceDateFilters = (appliedFilters ?? []).filter((filter) =>
        MAINTENANCE_DATE_FILTER_KEYS.includes(filter.key),
      )
      const backendFilters = (appliedFilters ?? []).filter(
        (filter) => !MAINTENANCE_DATE_FILTER_KEYS.includes(filter.key),
      )
      if (backendFilters.length > 0) {
        params.filters = JSON.stringify(backendFilters)
      }

      const response = await axios.get<BackendMachineListItem[]>(
        `${API_BASE_URL}/equipment`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        },
      )

      const filterGroups = new Map<string, any[]>()
      maintenanceDateFilters.forEach((filter) => {
        const group = filterGroups.get(filter.key) ?? []
        group.push(filter)
        filterGroups.set(filter.key, group)
      })

      const filteredItems = response.data.filter((item) =>
        Array.from(filterGroups.entries()).every(([key, groupFilters]) =>
          groupFilters.some((filter) =>
            machineMatchesMaintenanceDateFilter(
              key === 'lastMaintenance'
                ? item.lastMaintenanceDate
                : item.nextMaintenanceDate,
              filter,
            ),
          ),
        ),
      )

      const fetchedMachines: MachineData[] = filteredItems.map((item) => ({
        id: item.id,
        machineName: item.name,
        model: item.model,
        costPerHour: formatCurrency(item.costPerHour),
        lastMaintenance: formatDate(item.lastMaintenanceDate),
        nextMaintenance: formatDate(item.nextMaintenanceDate),
      }))

      fetchedMachines.sort((a, b) => b.id - a.id)
      setMachines(fetchedMachines)
    } catch (err) {
      console.error('Erro ao buscar dados das máquinas:', err)
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Não autorizado. Por favor, faça login novamente.')
        navigate('/login')
      } else {
        setError('Não foi possível carregar os equipamentos. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }, [search, appliedFilters, refresh, navigate, formatDate, formatCurrency])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (machineId: number) => {
    setSelectedMachineId(machineId)
    setShowViewModal(true)
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedMachineId(null)
  }

  const handleDeleteClick = (machineId: number) => {
    if (!canDeleteEquipment()) {
      setFeedback({
        isOpen: true,
        message: 'Você não tem permissão para deletar equipamentos. Apenas usuários autorizados podem realizar esta ação.',
        statusOk: false,
      })
      return
    }
    setMachineToDelete(machineId)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (machineToDelete === null) return
    const machineId = machineToDelete
    setShowDeleteModal(false)
    setMachineToDelete(null)

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        message: 'Sessão expirada. Por favor, faça login novamente.',
        statusOk: false,
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      await axios.delete(`${API_BASE_URL}/equipment/${machineId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setMachines((prevMachines) =>
        prevMachines.filter((machine) => machine.id !== machineId),
      )
      setFeedback({
        isOpen: true,
        message: 'Equipamento excluído com sucesso!',
        statusOk: true,
      })
    } catch (err) {
      console.error(`Erro ao excluir máquina ${machineId}:`, err)
      let errorMessage = 'Falha ao excluir o equipamento. Tente novamente.'
      if (axios.isAxiosError(err)) {
        const backendMessage = err.response?.data?.message || ''
        if (
          backendMessage.includes('constraint') ||
          backendMessage.includes('foreign key') ||
          backendMessage.includes('fkey') ||
          backendMessage.includes('foreign-key')
        ) {
          errorMessage = 'O equipamento não pode ser excluído pois possui etapas de produção ou serviços vinculados a ele.'
        } else if (err.response?.status === 401) {
          setFeedback({
            isOpen: true,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
            statusOk: false,
          })
          navigate('/login')
          return
        } else if (err.response?.status === 403) {
          errorMessage = 'Você não tem permissão para deletar este equipamento.'
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }
      }
      setFeedback({
        isOpen: true,
        message: errorMessage,
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setMachineToDelete(null)
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  return (
    <TableRoot>
      <StandardDataTable
        columns={columns}
        data={machines}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        isLoading={loading}
        exportFileName='equipamentos'
        emptyMessage='Nenhum equipamento encontrado.'
        rowActions={(machine) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleViewDetails(machine.id) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/equipment/edit/${machine.id}`) },
          {
            label: 'Excluir',
            icon: <Trash2 size={16} />,
            onClick: () => handleDeleteClick(machine.id),
            disabled: !canDeleteEquipment(),
            title: canDeleteEquipment() ? undefined : 'Sem permissão para excluir',
          },
        ]}
      />

      {showViewModal && selectedMachineId !== null && (
        <ViewEquipmentModal
          machineId={selectedMachineId}
          onClose={handleCloseViewModal}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Equipamento"
        message={`Tem certeza que deseja excluir a máquina com ID: ${machineToDelete}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: loading ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
    </TableRoot>
  )
}

export default EquipmentTable
